import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Field, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";
import useAuthStore from "../../stores/useAuthStore";
import {
    initLoginFlow,
    submitPasswordLogin,
    submitOidcFlow,
    extractCsrfToken,
    KratosError,
} from "../../services/kratos";
import { GoogleIcon, LockIcon } from "../../components/Icons/AuthIcons";
import styles from "./SignIn.module.scss";

interface SignInFormValues {
    email: string;
    password: string;
}

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .matches(/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/)
        .required(),
    password: Yup.string().required(),
});

const initialValues: SignInFormValues = {
    email: "",
    password: "",
};

const SignInFormikUseEffect = ({
    onValidChange,
}: {
    onValidChange: (isValid: boolean) => void;
}) => {
    const { values, errors, touched } = useFormikContext<SignInFormValues>();
    useEffect(() => {
        const isValid = Boolean(
            values.email &&
            values.password &&
            !errors.email &&
            !errors.password &&
            touched.email,
        );
        onValidChange(isValid);
    }, [values, errors, touched, onValidChange]);
    return null;
};

const SignIn = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const checkSession = useAuthStore((state) => state.checkSession);
    const session = useAuthStore((state) => state.session);

    const [disabledButton, setDisabledButton] = useState(true);
    const [apiError, setApiError] = useState("");
    const [googleLoading, setGoogleLoading] = useState(false);

    useEffect(() => {
        if (session) {
            navigate("/");
        }
    }, [session, navigate]);

    const translateKratosError = (error: unknown): string => {
        if (error instanceof KratosError) {
            return error.messageIds
                .map((id) => t(`auth.kratosErrors.${id}`, t("auth.kratosErrors.fallback")))
                .join(". ");
        }
        return error instanceof Error ? error.message : t("auth.kratosErrors.fallback");
    };

    const handleSubmit = async (values: SignInFormValues) => {
        setApiError("");
        try {
            const flow = await initLoginFlow();
            const csrfToken = extractCsrfToken(flow);
            await submitPasswordLogin(flow.id, values.email, values.password, csrfToken);
            await checkSession();
        } catch (error) {
            setApiError(translateKratosError(error));
        }
    };

    const handleGoogleLogin = async () => {
        setApiError("");
        setGoogleLoading(true);
        try {
            const flow = await initLoginFlow(window.location.origin + "/auth/callback");
            const csrfToken = extractCsrfToken(flow);
            const response = await submitOidcFlow(flow.id, "google", csrfToken);
            if (response.redirect_browser_to) {
                window.location.href = response.redirect_browser_to;
                return;
            }
            await checkSession();
        } catch (error) {
            setApiError(translateKratosError(error));
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({ errors, touched, setFieldTouched }) => (
                <Form className={styles._container}>
                    <SignInFormikUseEffect
                        onValidChange={(isValid) => setDisabledButton(!isValid)}
                    />
                    <div className={styles.wrapper}>
                        <h1>{t("auth.login.title")}</h1>

                        <div className={styles.itemGroups}>
                            <div className={styles.itemGroup}>
                                <div
                                    className={`${styles.inputItem} ${
                                        errors.email && touched.email ? styles.inputError : ""
                                    }`}
                                >
                                    <div className={styles.labelIcon}>
                                        <Icon.Email
                                            size={22}
                                            color={
                                                errors.email && touched.email
                                                    ? "#CC1818"
                                                    : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div>{t("auth.login.email")}</div>
                                    </div>
                                    <div className={styles.input}>
                                        <Field
                                            name="email"
                                            onInput={() =>
                                                setFieldTouched("email", true, true)
                                            }
                                            type="email"
                                            placeholder="E-mail"
                                        />
                                    </div>
                                </div>

                                <div
                                    className={`${styles.inputItem} ${
                                        errors.password && touched.password
                                            ? styles.inputError
                                            : ""
                                    }`}
                                >
                                    <div className={styles.labelIcon}>
                                        <LockIcon
                                            color={
                                                errors.password && touched.password
                                                    ? "#CC1818"
                                                    : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div>{t("auth.login.password")}</div>
                                    </div>
                                    <div className={styles.input}>
                                        <Field
                                            name="password"
                                            onInput={() =>
                                                setFieldTouched("password", true, true)
                                            }
                                            type="password"
                                            placeholder={t("auth.login.password")}
                                        />
                                    </div>
                                </div>
                            </div>

                            {apiError && (
                                <div className={styles.apiError}>
                                    <p>{apiError}</p>
                                </div>
                            )}
                        </div>

                        <button
                            disabled={disabledButton}
                            type="submit"
                            className={`btn btn-primary ${styles.submitButton}`}
                        >
                            {t("auth.login.submit")}
                        </button>

                        <div className={styles.divider}>{t("auth.or")}</div>

                        <button
                            type="button"
                            className={styles.googleButton}
                            onClick={handleGoogleLogin}
                            disabled={googleLoading}
                        >
                            <GoogleIcon />
                            {t("auth.login.google")}
                        </button>

                        <div className={styles.linkText}>
                            {t("auth.login.noAccount")}{" "}
                            <Link to="/auth/sign-up">{t("auth.login.register")}</Link>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default SignIn;
